import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Flex,
  Box,
  Image,
  Grid,
  GridItem,
  useColorModeValue,
  VStack,
  HStack,
  Button,
  Icon,
  Text,
  Tooltip,
  keyframes,
} from "@chakra-ui/react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation } from "react-router-dom";
import { MdModeEdit, MdElectricBolt } from "react-icons/md";
import { FaPlug } from "react-icons/fa";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

import "../../../../assets/css/chargePoints.css";

import { updateConnectorDrawerState } from "atoms/updateConnectorDrawerState";
import UpdateChargePointConnectorDrawer from "../components/UpdateChargePointConnectorDrawer";
import RemoteOperations from "../components/RemoteOperations";
import StartCharging from "../components/StartCharging";
import OngoingSession from "../components/OngoingSession";
import QrCode from "../components/QrCode";
import UnlockConnector from "../components/operatiions/UnlockConnector";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";

import ccstype2 from "../../../../assets/img/chargePointsTab/ccstype2.png";
import AC1 from "../../../../assets/img/chargePointsTab/actype1.png";
import AC2 from "../../../../assets/img/chargePointsTab/ac2.png";
import CDMO from "../../../../assets/img/chargePointsTab/chademo.png";
import DOMESTIC_D from "../../../../assets/img/chargePointsTab/domestic_d.png";

// ─── Status config ────────────────────────────────────────────────────────────

const statusConfigMap = {
  Charging: {
    color: "white",
    bg: "linear-gradient(135deg, #f57d36 0%, #ff9966 100%)",
    solidColor: "#f57d36",
    dot: "#f57d36",
    accentBar: "linear-gradient(180deg, #f57d36 0%, #ff9966 100%)",
    glow: "rgba(245, 125, 54, 0.18)",
    subtleBg: "rgba(245, 125, 54, 0.04)",
    label: "Charging",
    pulse: true,
  },
  Preparing: {
    color: "white",
    bg: "linear-gradient(135deg, #f5a623 0%, #f7b955 100%)",
    solidColor: "#f5a623",
    dot: "#f5a623",
    accentBar: "linear-gradient(180deg, #f5a623 0%, #f7b955 100%)",
    glow: "rgba(245, 166, 35, 0.15)",
    subtleBg: "rgba(245, 166, 35, 0.04)",
    label: "Preparing",
    pulse: false,
  },
  Available: {
    color: "white",
    bg: "linear-gradient(135deg, #38A169 0%, #68D391 100%)",
    solidColor: "#38A169",
    dot: "#38A169",
    accentBar: "linear-gradient(180deg, #38A169 0%, #68D391 100%)",
    glow: "rgba(56, 161, 105, 0.14)",
    subtleBg: "rgba(56, 161, 105, 0.04)",
    label: "Available",
    pulse: false,
  },
  default: {
    color: "white",
    bg: "linear-gradient(135deg, #718096 0%, #A0AEC0 100%)",
    solidColor: "#718096",
    dot: "#718096",
    accentBar: "linear-gradient(180deg, #718096 0%, #A0AEC0 100%)",
    glow: "rgba(113, 128, 150, 0.12)",
    subtleBg: "rgba(113, 128, 150, 0.04)",
    label: "Unavailable",
    pulse: false,
  },
};

const getStatusConfig = (status) =>
  statusConfigMap[status] || statusConfigMap.default;

// ─── Connector helpers ────────────────────────────────────────────────────────

const getConnectorTypeName = (connector) => {
  const type = connector?.Connectors?.[0]?.Standard || connector?.ConnectorType;
  switch (type) {
    case "AC Type 1":    return "AC Type 1";
    case "AC Type 2":    return "AC Type 2";
    case "CCS Type 2":   return "CCS Type 2";
    case "CHAdeMO":      return "CHAdeMO";
    case "DOMESTIC_D":   return "3 Pin Socket";
    default:             return type || "Unknown";
  }
};

const getConnectorImage = (connector) => {
  const type = connector?.Connectors?.[0]?.Standard || connector?.ConnectorType;
  switch (type) {
    case "AC Type 1":  return AC1;
    case "AC Type 2":  return AC2;
    case "CCS Type 2": return ccstype2;
    case "CHAdeMO":    return CDMO;
    case "DOMESTIC_D": return DOMESTIC_D;
    default:           return ccstype2;
  }
};

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulseDot = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(245, 125, 54, 0.6); }
  70%  { box-shadow: 0 0 0 8px rgba(245, 125, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 125, 54, 0); }
`;

// ─── Styled primitives ────────────────────────────────────────────────────────

const PanelBox = styled(Box)`
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(14px);
  border-radius: 16px;
  border: 1px solid rgba(245, 125, 54, 0.12);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  height: 100%;
  min-width: 0;
  overflow: hidden;

  &:hover {
    border-color: rgba(245, 125, 54, 0.28);
  }
`;

// ─── ConnectorIdentityPanel ───────────────────────────────────────────────────
// Left column: connector image, type label, power spec, QR button

const ConnectorIdentityPanel = ({ connector, statusConfig, refreshHandler }) => {
  const connectorName = getConnectorTypeName(connector);
  const connectorImg = getConnectorImage(connector);
  const qrValue = connector?.EvseId + "-" + connector?.EvseSerialId;
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const canUnlock = connector?.Status && connector.Status !== "Unavailable";

  return (
    <PanelBox p={{ base: 2, md: 2.5, lg: 3 }}>
      <VStack spacing={{ base: 2, md: 2.5 }} align="center" h="100%" justify="center">
        {/* Connector image with status-coloured halo */}
        <Box position="relative">
          <Box
            borderRadius="full"
            bg={statusConfig.subtleBg}
            p={{ base: 2, md: 2.5, lg: 3 }}
            border="2px solid"
            borderColor={`${statusConfig.solidColor}30`}
            boxShadow={`0 0 0 4px ${statusConfig.glow}`}
            transition="all 0.3s ease"
          >
            <Image
              src={connectorImg}
              alt={connectorName}
              w={{ base: "36px", md: "44px", lg: "56px" }}
              h={{ base: "36px", md: "44px", lg: "56px" }}
              objectFit="contain"
            />
          </Box>
          {/* Live status dot */}
          <Box
            position="absolute"
            bottom="1px"
            right="1px"
            w={{ base: "8px", md: "10px" }}
            h={{ base: "8px", md: "10px" }}
            borderRadius="full"
            bg={statusConfig.dot}
            border="2px solid white"
            boxShadow={`0 0 4px ${statusConfig.dot}`}
            animation={statusConfig.pulse ? `${pulseDot} 2s infinite` : undefined}
          />
        </Box>

        {/* QR Code button */}
        <Box w="100%">
          <QrCode value={qrValue} compact />
        </Box>

        {/* Unlock Connector — only when connector is reachable */}
        {canUnlock && (
          <Box w="100%">
            <UnlockConnector connector={connector} refresh={refreshHandler} compact />
          </Box>
        )}
      </VStack>
    </PanelBox>
  );
};

// ─── ConnectorSection ─────────────────────────────────────────────────────────

const ConnectorSection = ({ connector, index, refreshHandler, highlight }) => {
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit = accessLevels[role]?.accessLevel <= 2;
  const setUpdateDrawerState = useSetRecoilState(updateConnectorDrawerState);

  const labelColor = useColorModeValue("gray.800", "gray.100");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const statusConfig = getStatusConfig(connector?.Status);
  const gunLabel = `Gun ${connector?.EvseSerialId}`;
  const connectorName = getConnectorTypeName(connector);
  const isCharging = connector?.Status === "Charging";

  const editHandler = () => {
    setUpdateDrawerState({ isOpen: true, activeId: connector?.Id });
  };

  return (
    <Box
      id={`connector-${connector?.Id || index}`}
      as={motion.div}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      position="relative"
      borderRadius="2xl"
      overflow="hidden"
      bg="rgba(255, 255, 255, 0.88)"
      backdropFilter="blur(16px)"
      border="1px solid"
      borderColor={highlight ? statusConfig.solidColor : "rgba(0,0,0,0.07)"}
      boxShadow={
        highlight
          ? `0 0 0 2px ${statusConfig.solidColor}50, 0 12px 40px ${statusConfig.glow}`
          : `0 4px 24px rgba(0,0,0,0.06)`
      }
      mb={{ base: 4, md: 5 }}
      _hover={{
        boxShadow: `0 8px 32px ${statusConfig.glow}, 0 4px 16px rgba(0,0,0,0.06)`,
        borderColor: `${statusConfig.solidColor}40`,
      }}
    >
      {/* Left status accent bar */}
      <Box
        position="absolute"
        left={0}
        top={0}
        bottom={0}
        w="3px"
        bg={statusConfig.accentBar}
        borderLeftRadius="2xl"
        zIndex={2}
      />

      {/* Inner content (offset for accent bar) */}
      <Box pl={{ base: "12px", md: "16px" }} pr={{ base: 2, sm: 3, md: 4 }} py={{ base: 2, sm: 2.5, md: 3 }}>

        {/* ── Header ── */}
        <Flex
          direction="row"
          align="center"
          justify="space-between"
          gap={2}
          mb={{ base: 2.5, md: 3 }}
          flexWrap="wrap"
        >
          {/* Left: identity */}
          <HStack spacing={2} flexWrap="wrap" align="center">
            {/* Gun number pill */}
            <HStack
              spacing={1.5}
              bg={statusConfig.subtleBg}
              border="1px solid"
              borderColor={`${statusConfig.solidColor}25`}
              borderRadius="md"
              px={2.5}
              py={1}
            >
              <Icon
                as={FaPlug}
                color={statusConfig.solidColor}
                boxSize={3}
              />
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="800"
                color={labelColor}
                letterSpacing="-0.3px"
              >
                {gunLabel}
              </Text>
            </HStack>

            {/* Status pill — boxy, matches gun number pill */}
            <HStack
              spacing={1.5}
              bg={statusConfig.subtleBg}
              border="1px solid"
              borderColor={`${statusConfig.solidColor}35`}
              borderRadius="md"
              px={2.5}
              py={1}
            >
              <Icon
                as={MdElectricBolt}
                color={statusConfig.solidColor}
                boxSize={3}
                animation={isCharging ? `${pulseDot} 1.5s infinite` : undefined}
              />
              <Text
                fontSize="xs"
                fontWeight="700"
                color={statusConfig.solidColor}
                textTransform="capitalize"
                letterSpacing="0.1px"
              >
                {connector?.Status || "Unknown"}
              </Text>
            </HStack>
          </HStack>

          {/* Right: actions */}
          <HStack spacing={2} flexShrink={0} ml="auto">
            <RoleBasedRender roles={onlyAdmins}>
              <Tooltip
                hasArrow
                label="Edit connector settings"
                placement="top"
                bg={statusConfig.solidColor}
                color="white"
              >
                <Button
                  size="xs"
                  leftIcon={<MdModeEdit size={11} />}
                  variant="outline"
                  borderColor={`${statusConfig.solidColor}50`}
                  color={statusConfig.solidColor}
                  borderRadius="md"
                  onClick={editHandler}
                  isDisabled={!canEdit}
                  _hover={{
                    bg: statusConfig.subtleBg,
                    borderColor: statusConfig.solidColor,
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  transition="all 0.2s ease"
                  fontSize="xs"
                  h={7}
                  px={2.5}
                >
                  Edit
                </Button>
              </Tooltip>
            </RoleBasedRender>
          </HStack>
        </Flex>

        {/* ── Body grid ── */}
        <Grid
          templateColumns={{
            base: "90px minmax(0, 1fr)",   // mobile: identity | charging
            md:   "110px minmax(0, 1fr)",   // tablet: identity | charging, remote below
            lg:   "120px minmax(0, 2fr) minmax(0, 3.5fr)", // desktop: all 3
          }}
          gap={{ base: 2, md: 2.5, lg: 3 }}
          alignItems="stretch"
        >
          {/* Column 1 — Identity panel */}
          <GridItem rowSpan={{ base: 1, md: 1, lg: 1 }}>
            <ConnectorIdentityPanel
              connector={connector}
              statusConfig={statusConfig}
              refreshHandler={refreshHandler}
            />
          </GridItem>

          {/* Column 2 — Charging controls */}
          <GridItem>
            {isCharging ? (
              <OngoingSession refresh={refreshHandler} connector={connector} />
            ) : (
              <StartCharging refresh={refreshHandler} connector={connector} />
            )}
          </GridItem>

          {/* Column 3 — Remote operations:
              base/md: spans both columns (full row below identity+charging)
              lg: own column */}
          <GridItem colSpan={{ base: 2, md: 2, lg: 1 }}>
            <RemoteOperations connector={connector} refresh={refreshHandler} />
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
};

// ─── Connectors (list) ────────────────────────────────────────────────────────

const Connectors = ({ data, refreshHandler }) => {
  const location = useLocation();
  const [highlightId, setHighlightId] = useState(null);
  const [updateDrawerState] = useRecoilState(updateConnectorDrawerState);
  const refreshRef = useRef(refreshHandler);

  // Keep the latest refreshHandler reference without resetting the interval
  useEffect(() => {
    refreshRef.current = refreshHandler;
  }, [refreshHandler]);

  const cidFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("cid");
  }, [location.search]);

  // Poll for fresh connector data so statuses stay up to date across all sections
  useEffect(() => {
    const interval = setInterval(() => {
      refreshRef.current?.();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (cidFromUrl && data.length > 0) {
      const matched = data.find(
        (c) => String(c.EvseSerialId) === String(cidFromUrl),
      );
      if (matched) {
        setHighlightId(matched.Id);
        const el = document.getElementById(`connector-${matched.Id}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  }, [cidFromUrl, data]);

  return (
    <Flex flexDirection="column" position="relative" width="100%">
      <VStack align="stretch" spacing={{ base: 3, md: 4 }} width="100%">
        {data.map((connector, index) => (
          <ConnectorSection
            key={connector.Id || index}
            connector={connector}
            index={index}
            refreshHandler={refreshHandler}
            highlight={highlightId === connector.Id}
          />
        ))}
      </VStack>

      {updateDrawerState?.isOpen && (
        <UpdateChargePointConnectorDrawer id={updateDrawerState?.activeId} />
      )}
    </Flex>
  );
};

export default Connectors;
